import React, { useEffect, useMemo, useState } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../../firebase-client';
function compareBoatsByTypeThenName(a, b) {
  const typeCompare = (a?.type || '').localeCompare(b?.type || '', undefined, {
    sensitivity: 'base'
  });
  if (typeCompare !== 0) return typeCompare;
  return (a?.name || '').localeCompare(b?.name || '', undefined, {
    sensitivity: 'base'
  });
}
export default function EditRowingBoatsForm({
  isOpen,
  onClose,
  onSave,
  // optional callback after successful save
  teamId,
  // required to target the subcollection
  allTags = [],
  focusBoatId = null
}) {
  const KG_TO_LB = 2.2046226218;
  const [resources, setResources] = useState([]); // displayed boats (without deleted ones)
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showAll, setShowAll] = useState(!focusBoatId);
  const [weightUnit, setWeightUnit] = useState('kg');
  const [weightEntryMode, setWeightEntryMode] = useState('crewTotal');

  // Track original IDs so we can detect deletions
  const [originalIds, setOriginalIds] = useState(new Set());
  const [deletedIds, setDeletedIds] = useState(new Set());

  // --- helpers --------------------------------------------------------------

  // Firestore-safe: returns a number or null (never undefined)
  const seatsForType = type => {
    const t = (type || '').toLowerCase();
    if (t === '1x') return 1;
    if (t === '2x' || t === '2-') return 2;
    if (t === '4x' || t === '4x+' || t === '4-' || t === '4+') return 4;
    if (t === '8+') return 8;
    if (t === 'ergo') return 1; // per machine
    if (t === 'launch') return 0; // not bookable by crew
    return null; // important: Firestore rejects undefined, accepts null
  };

  // Remove any undefined values (and undefined entries in arrays)
  const sanitizeForFirestore = obj => {
    const out = {};
    for (const [k, v] of Object.entries(obj)) {
      if (v === undefined) continue;
      if (Array.isArray(v)) out[k] = v.filter(x => x !== undefined);else out[k] = v;
    }
    return out;
  };
  const cleanTags = arr => Array.isArray(arr) ? arr.filter(t => typeof t === 'string' && t.trim().length > 0) : [];
  const smallActionButtonStyle = {
    border: '1px solid currentColor',
    borderRadius: '0.5rem',
    background: 'transparent',
    color: 'inherit',
    opacity: 0.9
  };
  const removeButtonStyle = {
    ...smallActionButtonStyle,
    padding: '0.12rem 0.45rem',
    fontSize: '0.75rem',
    lineHeight: 1,
    alignSelf: 'flex-end',
    marginBottom: 1
  };
  const cleanOptionalText = value => {
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    return trimmed.length ? trimmed : null;
  };
  const cleanOptionalNumber = value => {
    if (value === '' || value === null || value === undefined) return null;
    const n = Number(value);
    if (!Number.isFinite(n)) return null;
    return n;
  };
  const normalizeCrewWeightRange = (minValue, maxValue) => {
    const min = cleanOptionalNumber(minValue);
    const max = cleanOptionalNumber(maxValue);
    if (min !== null && max !== null && min > max) {
      return {
        min: max,
        max: min
      };
    }
    return {
      min,
      max
    };
  };
  const kgToDisplayUnit = kgValue => {
    const kg = cleanOptionalNumber(kgValue);
    if (kg === null) return '';
    if (weightUnit === 'lb') return Math.round(kg * KG_TO_LB);
    return kg;
  };
  const displayUnitToKg = displayValue => {
    const n = cleanOptionalNumber(displayValue);
    if (n === null) return '';
    if (weightUnit === 'lb') {
      return Math.round(n / KG_TO_LB * 10) / 10;
    }
    return n;
  };
  const roundToOneDecimal = value => Math.round(Number(value) * 10) / 10;
  const seatsForBoat = boat => {
    const fromBoat = cleanOptionalNumber(boat?.seats);
    if (fromBoat !== null) return fromBoat;
    return seatsForType(boat?.type);
  };
  const crewWeightKgToDisplayValue = (kgValue, boat) => {
    const kg = cleanOptionalNumber(kgValue);
    if (kg === null) return '';
    const seats = seatsForBoat(boat);
    const usesAverage = weightEntryMode === 'avgRower' && Number.isFinite(seats) && seats > 0;
    const normalizedKg = usesAverage ? roundToOneDecimal(kg / seats) : kg;
    return kgToDisplayUnit(normalizedKg);
  };
  const displayValueToCrewWeightKg = (displayValue, boat) => {
    const kgValue = displayUnitToKg(displayValue);
    if (kgValue === '') return '';
    const seats = seatsForBoat(boat);
    const usesAverage = weightEntryMode === 'avgRower' && Number.isFinite(seats) && seats > 0;
    if (!usesAverage) return roundToOneDecimal(kgValue);
    return roundToOneDecimal(Number(kgValue) * seats);
  };
  const boatsRefPath = useMemo(() => teamId ? `teams/${teamId}/boats` : null, [teamId]);

  // --- effects --------------------------------------------------------------

  useEffect(() => {
    if (!isOpen || !boatsRefPath) return;
    (async () => {
      setLoading(true);
      try {
        const snap = await getDocs(collection(db, boatsRefPath));
        const boats = snap.docs.map(d => ({
          id: d.id,
          ...d.data()
        }));
        boats.sort(compareBoatsByTypeThenName);
        setResources(boats);
        setOriginalIds(new Set(boats.map(b => b.id)));
        setDeletedIds(new Set());
      } catch (err) {} finally {
        setLoading(false);
      }
    })();
  }, [isOpen, boatsRefPath]);
  useEffect(() => {
    setShowAll(!focusBoatId);
  }, [focusBoatId]);

  // --- handlers -------------------------------------------------------------

  const add = () => setResources(prev => [...prev, {
    id: undefined,
    name: '',
    type: '1x',
    status: 'active',
    tagsAllowed: [],
    crewWeightMinKg: '',
    crewWeightMaxKg: '',
    registrationNumber: ''
  }]);
  const patch = (i, p) => setResources(prev => prev.map((r, idx) => idx === i ? {
    ...r,
    ...p
  } : r));
  const remove = i => {
    const resource = resources[i];
    if (!window.confirm(
      resource?.id
        ? `Remove "${resource.name || 'this boat'}" from the fleet? It will only be deleted when you save, and can be restored before then.`
        : 'Discard this unsaved boat draft?'
    )) return;
    if (resource?.id) {
      setDeletedIds(d => new Set(d).add(resource.id));
      return;
    }
    setResources(prev => {
      return prev.filter((_, idx) => idx !== i);
    });
  };
  const restore = id => setDeletedIds(current => {
    const next = new Set(current);
    next.delete(id);
    return next;
  });
  const toggleTag = (i, tag) => {
    setResources(prev => prev.map((r, idx) => {
      if (idx !== i) return r;
      const current = new Set(cleanTags(r.tagsAllowed));
      current.has(tag) ? current.delete(tag) : current.add(tag);
      return {
        ...r,
        tagsAllowed: Array.from(current)
      };
    }));
  };
  const patchCrewWeight = (idx, key, inputValue) => {
    if (!['crewWeightMinKg', 'crewWeightMaxKg'].includes(key)) return;
    if (inputValue === '') {
      patch(idx, {
        [key]: ''
      });
      return;
    }
    const boat = resources[idx];
    patch(idx, {
      [key]: displayValueToCrewWeightKg(inputValue, boat)
    });
  };
  const handleSubmit = async e => {
    e.preventDefault();
    if (!boatsRefPath) return;
    setSaving(true);
    try {
      // Determine creates & updates
      const toCreate = [];
      const toUpdate = [];
      for (const r of resources) {
        const crewRange = normalizeCrewWeightRange(r.crewWeightMinKg, r.crewWeightMaxKg);
        const payloadRaw = {
          name: r.name || '',
          type: r.type || '1x',
          status: r.status || 'active',
          tagsAllowed: cleanTags(r.tagsAllowed),
          crewWeightMinKg: crewRange.min,
          crewWeightMaxKg: crewRange.max,
          weightClass: null,
          // legacy field no longer used
          registrationNumber: cleanOptionalText(r.registrationNumber),
          seats: seatsForType(r.type),
          // null if unknown
          updatedAt: serverTimestamp()
        };
        const payload = sanitizeForFirestore(payloadRaw);
        if (!r.id) {
          toCreate.push({
            ...payload,
            createdAt: serverTimestamp()
          });
        } else if (!deletedIds.has(r.id)) {
          toUpdate.push({
            id: r.id,
            payload
          });
        }
      }

      // Deletes: any originalIds that are not in current list OR explicitly marked deleted
      const currentIds = new Set(resources.filter(r => r.id).map(r => r.id));
      const toDelete = new Set([...originalIds].filter(id => !currentIds.has(id) || deletedIds.has(id)));

      // Execute writes
      const ref = collection(db, boatsRefPath);

      // creates
      for (const payload of toCreate) {
        await addDoc(ref, payload);
      }

      // updates
      for (const u of toUpdate) {
        await updateDoc(doc(db, `${boatsRefPath}/${u.id}`), u.payload);
      }

      // deletes
      for (const id of toDelete) {
        await deleteDoc(doc(db, `${boatsRefPath}/${id}`));
      }

      // optional callback to parent
      onSave?.();

      // close after save
      onClose?.();
    } catch (err) {} finally {
      setSaving(false);
    }
  };
  const visibleResources = useMemo(() => {
    const indexed = resources.map((r, idx) => ({
      r,
      idx
    })).filter(({ r }) => !r.id || !deletedIds.has(r.id));
    if (!focusBoatId || showAll) return indexed;
    return indexed.filter(({
      r
    }) => r.id === focusBoatId);
  }, [resources, deletedIds, focusBoatId, showAll]);
  if (!isOpen) return null;
  const usingLb = weightUnit === 'lb';
  const deletedResources = resources.filter(resource => resource.id && deletedIds.has(resource.id));
  const totalBoats = resources.length - deletedResources.length;

  // --- render ---------------------------------------------------------------

  return <form onSubmit={handleSubmit} className="modal-form">
      <h2>Manage Boats</h2>
      <div className="text-muted boat-admin-subtitle">
        {totalBoats} boat{totalBoats === 1 ? '' : 's'} in this fleet.
      </div>
      {deletedResources.length > 0 && <section className="sailing-boats-modal__pending-deletions">
          <div>
            <h3>{deletedResources.length} boat{deletedResources.length === 1 ? '' : 's'} will be deleted</h3>
            <p>Deletion only happens when you save.</p>
          </div>
          <div className="sailing-boats-modal__restore-list">
            {deletedResources.map(resource => <button key={resource.id} type="button" className="button button-secondary button--xs" onClick={() => restore(resource.id)}>
                Restore {resource.name || 'unnamed boat'}
              </button>)}
          </div>
        </section>}

      <section className="form-section boat-request-panel">
        <div className="boat-request-section-head">
          <h3>Weight Settings</h3>
        </div>
        <div className="boat-admin-settings-grid">
          <label className="form-label">
            <span style={{
            display: 'block'
          }}>Weight entry mode</span>
            <select value={weightEntryMode} onChange={e => setWeightEntryMode(e.target.value)}>
              <option value="crewTotal">Crew total</option>
              <option value="avgRower">Average rower</option>
            </select>
          </label>
          <label className="form-label">
            <span style={{
            display: 'block'
          }}>Crew weight unit</span>
            <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 10,
            marginTop: 2,
            padding: '0.3rem 0.55rem',
            border: '1px solid rgba(0, 0, 0, 0.16)',
            borderRadius: 999
          }}>
              <span style={{
              fontSize: '0.8rem',
              fontWeight: 600,
              minWidth: 18,
              textAlign: 'right',
              opacity: usingLb ? 0.55 : 1,
              transition: 'opacity 180ms ease'
            }}>
                kg
              </span>
              <span style={{
              position: 'relative',
              display: 'inline-block',
              width: 46,
              height: 24,
              flexShrink: 0
            }}>
                <input type="checkbox" checked={usingLb} onChange={e => setWeightUnit(e.target.checked ? 'lb' : 'kg')} aria-label="Toggle weight unit between kilograms and pounds" style={{
                position: 'absolute',
                inset: 0,
                margin: 0,
                opacity: 0,
                cursor: 'pointer',
                zIndex: 2
              }} />
                <span style={{
                position: 'absolute',
                inset: 0,
                borderRadius: 24,
                background: usingLb ? 'rgba(59, 130, 246, 0.9)' : 'rgba(0, 0, 0, 0.24)',
                transition: 'background-color 220ms ease'
              }} />
                <span style={{
                position: 'absolute',
                top: 3,
                left: 3,
                width: 18,
                height: 18,
                borderRadius: '50%',
                background: '#fff',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.28)',
                transform: usingLb ? 'translateX(22px)' : 'translateX(0)',
                transition: 'transform 220ms cubic-bezier(0.22, 1, 0.36, 1)'
              }} />
              </span>
              <span style={{
              fontSize: '0.8rem',
              fontWeight: 600,
              minWidth: 16,
              opacity: usingLb ? 1 : 0.55,
              transition: 'opacity 180ms ease'
            }}>
                lb
              </span>
            </div>
          </label>
        </div>
      </section>

      {loading ? <div className="text-muted">Loading boats…</div> : <>
          {focusBoatId && !showAll && <div className="text-muted boat-admin-focus-note">
              Editing selected boat.
              <button type="button" className="button button--xs" onClick={() => setShowAll(true)} style={{
          marginLeft: 8,
          ...smallActionButtonStyle
        }}>
                Show all boats
              </button>
            </div>}

          {visibleResources.length === 0 && <div className="text-muted boat-admin-focus-note">
              That boat is no longer available.
            </div>}

          {visibleResources.map(({
        r,
        idx
      }) => <div key={r.id || `new-${idx}`} className="card boat-admin-card">
              <div className="form-row boat-admin-main-row">
                <label className="form-label boat-admin-name-field">
                  Name
                  <input value={r.name || ''} onChange={e => patch(idx, {
              name: e.target.value
            })} />
                </label>

                <label className="form-label boat-admin-type-field">
                  Type
                  <select value={r.type || '1x'} onChange={e => patch(idx, {
              type: e.target.value
            })}>
                    <option value="1x">1x</option>
                    <option value="2x">2x</option>
                    <option value="2-">2-</option>
                    <option value="4x">4x</option>
                    <option value="4x+">4x+</option>
                    <option value="4-">4-</option>
                    <option value="4+">4+</option>
                    <option value="8+">8+</option>
                    <option value="ergo">Ergo</option>
                    <option value="launch">Launch</option>
                    <option value="other">Other</option>
                  </select>
                </label>

                <label className="form-label boat-admin-status-field">
                  Status
                  <select value={r.status || 'active'} onChange={e => patch(idx, {
              status: e.target.value
            })}>
                    <option value="active">Active</option>
                    <option value="maintenance">Maintenance</option>
                  </select>
                </label>

                <button type="button" className="button button--xs" onClick={() => remove(idx)} style={removeButtonStyle}>
                  Remove
                </button>
              </div>

              <div className="form-row boat-admin-weight-row">
                <label className="form-label boat-admin-weight-field">
                  {weightEntryMode === 'avgRower' ? 'Avg rower weight min' : 'Crew weight min'} ({weightUnit})
                  <input type="number" min={0} step={1} value={crewWeightKgToDisplayValue(r.crewWeightMinKg, r)} onChange={e => patchCrewWeight(idx, 'crewWeightMinKg', e.target.value)} placeholder={weightEntryMode === 'avgRower' ? weightUnit === 'lb' ? 'e.g. 160' : 'e.g. 73' : weightUnit === 'lb' ? 'e.g. 110' : 'e.g. 50'} />
                </label>
                <label className="form-label boat-admin-weight-field">
                  {weightEntryMode === 'avgRower' ? 'Avg rower weight max' : 'Crew weight max'} ({weightUnit})
                  <input type="number" min={0} step={1} value={crewWeightKgToDisplayValue(r.crewWeightMaxKg, r)} onChange={e => patchCrewWeight(idx, 'crewWeightMaxKg', e.target.value)} placeholder={weightEntryMode === 'avgRower' ? weightUnit === 'lb' ? 'e.g. 198' : 'e.g. 90' : weightUnit === 'lb' ? 'e.g. 143' : 'e.g. 65'} />
                </label>
                <label className="form-label boat-admin-reg-field">
                  Registration number
                  <input value={r.registrationNumber || ''} onChange={e => patch(idx, {
              registrationNumber: e.target.value
            })} placeholder="e.g. GBR-12345" />
                </label>
              </div>
              {weightEntryMode === 'avgRower' && !(Number.isFinite(seatsForBoat(r)) && seatsForBoat(r) > 0) && <div className="text-muted" style={{
          fontSize: '0.85rem',
          marginTop: 4
        }}>
                  Average mode requires a seat count, so this boat currently uses crew total values.
                </div>}

              <div className="boat-admin-tags">
                <div className="text-muted" style={{
            fontSize: '0.9rem'
          }}>Allowed Tags</div>
                <div className="boat-admin-tag-grid">
                  {allTags.map(t => <label key={t.name} className="boat-admin-tag-item">
                      <input type="checkbox" checked={Array.isArray(r.tagsAllowed) && r.tagsAllowed.includes(t.name)} onChange={() => toggleTag(idx, t.name)} />
                      {t.name}
                    </label>)}
                </div>
              </div>
            </div>)}

          {(showAll || !focusBoatId) && <div className="boat-admin-add-row">
              <button type="button" className="button button--xs" onClick={add} style={smallActionButtonStyle}>
                + Add Boat
              </button>
            </div>}

          <div className="form-actions">
            <button type="button" className="button button--xs" onClick={onClose} style={smallActionButtonStyle}>
              Cancel
            </button>
            <button type="submit" className="button button--xs" disabled={saving} style={saving ? {
          ...smallActionButtonStyle,
          opacity: 0.5,
          cursor: 'not-allowed'
        } : smallActionButtonStyle}>
              {saving ? 'Saving…' : 'Save Boats'}
            </button>
          </div>
        </>}
    </form>;
}
